import { Money, GlobalSettings } from './lib';
import { DictionaryHelper } from './dictionary-helper';
import { TranslatePropertyHelper } from './languages/translation-properties-helper';
import { AgeRestrictionValidator } from './age/age-restriction-validator';
import { TicketType } from './ticket/ticket-type';

export class Product {
  id: number;
  name: string;
  price: Money;
  tax: number; // 0.19
  subProductId: number;
  accessTimeMinutes: number;
  hasImage: boolean;
  private internalDictionaryHelper: DictionaryHelper;
  additionalProperties: any;
  private translatePropertyHelper: TranslatePropertyHelper;

  private internalSaleAgeRestrictionValidator: AgeRestrictionValidator;
  private internalGroupAgeRestrictionValidator: AgeRestrictionValidator;

  subProducts: Product[];

  customPrice: Money;

  static isProduct(other: any): boolean {
    return other != null && other.constructor.name === Product.name;
  }

  static fromJson(data: any): Product {
    if (data == null || data.id === -1) {
      return null;
    }
    const obj = data;
    return new Product(
      obj.id,
      obj.name,
      Money.fromJSON(obj.price),
      obj.tax,
      obj.subProductId,
      obj.accessTimeMinutes,
      obj.hasImage,
      obj.additionalProperties,
    );
  }

  constructor(
    id: number,
    name: string,
    price: Money,
    tax: number,
    subProductId: number,
    accessTimeMinutes: number,
    hasImage: boolean,
    additionalProperties: any = {},
  ) {
    this.id = id;
    this.name = name;
    this.price = price;
    this.tax = tax;
    this.subProductId = subProductId;
    this.accessTimeMinutes = accessTimeMinutes;
    this.hasImage = hasImage;
    this.internalDictionaryHelper = new DictionaryHelper(additionalProperties);
    this.additionalProperties = additionalProperties;
    this.translatePropertyHelper = new TranslatePropertyHelper(additionalProperties ? additionalProperties.translations : null);
  }

  public toString(): string {
    let m = `Product. id: '${this.id}'. `;
    m += `name: '${this.name}'. `;
    m += `price: '${this.price}'. `;
    m += `tax: '${this.tax}'. `;
    m += `subProductId: '${this.subProductId}' `;
    m += `accessTimeMinutes: '${this.accessTimeMinutes}' `;
    m += `hasImage: '${this.hasImage}' `;
    m += `additionalProperties: ${this.internalDictionaryHelper.additionalProperties}. `;
    return m;
  }

  get displayBirthdayField(): boolean {
    return this.internalDictionaryHelper.getBoolean('display_birthday_field');
  }

  get isPartnerRequired(): boolean {
    return this.internalDictionaryHelper.getBoolean('is_partner_required');
  }

  get ticketIsDiscounted(): boolean {
    return this.internalDictionaryHelper.getBoolean('ticket_is_discounted');
  }

  get ticketIsSeasoned(): boolean {
    return this.internalDictionaryHelper.getBoolean('ticket_is_seasoned');
  }

  get quantity(): number {
    return this.internalDictionaryHelper.getNumber('quantity');
  }

  get groupSizeMin(): number {
    return this.internalDictionaryHelper.getNumber('group_size_min');
  }

  get groupSizeMax(): number {
    return this.internalDictionaryHelper.getNumber('group_size_max');
  }

  get ticketType(): string {
    return this.internalDictionaryHelper.getString('ticket_type');
  }

  get buyInOneClick(): boolean {
    return this.buyInOneClickJournal !== '';
  }

  get buyInOneClickJournal(): string {
    return this.internalDictionaryHelper.getString('buy_in_one_click');
  }

  get appointmentEnabled(): boolean {
    return this.internalDictionaryHelper.getBoolean('appointment_enabled');
  }

  get appointmentDateFrom(): Date {
    return this.internalDictionaryHelper.getDate('appointment_date_from_restriction');
  }

  get appointmentDateTo(): Date {
    return this.internalDictionaryHelper.getDate('appointment_date_to_restriction');
  }

  get appointmentKeywords(): string {
    return this.internalDictionaryHelper.getString('appointment_keywords');
  }

  get qtyMin(): number {
    return this.internalDictionaryHelper.getNumber('qty_min');
  }

  get qtyMax(): number {
    return this.internalDictionaryHelper.getNumber('qty_max');
  }

  get ticketDisplayName(): string {
    return this.internalDictionaryHelper.getString('ticket_display_name');
  }

  get subProductIds(): number[] {
    return this.internalDictionaryHelper.getNumberArray('additional_product_ids');
  }

  translateTicketDisplayName(language: string, defaultValue: string = ''): string {
    return this.translatePropertyHelper.getTranslationByFieldName(language, 'ticket_display_name', defaultValue);
  }

  translateName(language: string, defaultValue: string = ''): string {
    return this.translatePropertyHelper.getTranslationByFieldName(language, 'name', defaultValue);
  }

  get additionalProductIds(): number[] {
    return this.internalDictionaryHelper.getNumberArray('additional_product_ids');
  }

  get additionalProductsVariants(): any {
    return this.internalDictionaryHelper.getProperty('additional_products_variants');
  }

  get disableCreateTicket(): boolean {
    return this.internalDictionaryHelper.getBoolean('disable_create_ticket');
  }

  isValidForAge(age: number): boolean {
    if (!this.internalSaleAgeRestrictionValidator) {
      this.internalSaleAgeRestrictionValidator = AgeRestrictionValidator.fromString(
        this.internalDictionaryHelper.getString('sale_age_restriction'));
    }
    return this.internalSaleAgeRestrictionValidator.isAgeValid(age);
  }

  isValidForAgesGroup(ages: number[], groupSize: number): boolean {
    if (!this.internalGroupAgeRestrictionValidator) {
      this.internalGroupAgeRestrictionValidator = AgeRestrictionValidator.fromString(
        this.internalDictionaryHelper.getString('group_age_restriction'));
    }
    return this.internalGroupAgeRestrictionValidator.isAgesValid(ages, groupSize);
  }

  get rfidCard(): boolean {
    return this.internalDictionaryHelper.getString('ticket_type') === TicketType.Card;
  }

  get subProductsPrice(): Money {
    if (this.subProducts == null || this.subProducts.length === 0) {
      return Money.empty;
    }
    const totalPrice = this.subProducts.reduce((sum: number, product: Product) => sum + product.price.value, 0);
    return new Money(totalPrice, Money.getCurrencyCode(this.price));
  }

  get ticketParametersRequested(): boolean {
    return this.isPartnerRequired
      || (this.groupSizeMin > 0 && this.groupSizeMax > 0 && this.groupSizeMin !== this.groupSizeMax)
      || this.appointmentEnabled;
  }

  get isCard(): boolean {
    return this.internalDictionaryHelper.getString('ticket_type') === TicketType.Card;
  }

  get scanCardRequested(): boolean {
    return this.internalDictionaryHelper.getString('ticket_type') === TicketType.RequestCard;
  }

  get scanBarcodeRequested(): boolean {
    return this.internalDictionaryHelper.getString('ticket_type') === TicketType.ScanBarcode;
  }

  get vuAutomaticActionType(): string {
    return this.internalDictionaryHelper.getString('vu_automatic_action_type');
  }

  get scanCardRequestedToCheckIn(): boolean {
    return this.internalDictionaryHelper.getString('ticket_type') === TicketType.RequestCardToCheckIn;
  }

  get insertCardRequestedToCheckIn(): boolean {
    return this.internalDictionaryHelper.getString('ticket_type') === TicketType.InsertCardToCheckIn;
  }
}
